import { useState } from 'react';
import { AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';

export function CustomerSmsConsentPanel({ customer, onUpdated }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const consent = customer?.consent || {};
  const optedOut = customer?.sms_opted_out || Boolean(consent.sms_opt_out_at);
  const granted = consent.sms_consent === true && !optedOut;
  const latest = (customer?.consent_audit || [])[0];

  const updateConsent = async (smsConsent) => {
    setSaving(true); setError('');
    try {
      const { data } = await api.post(`/customers/${customer.id}/sms-consent`, { sms_consent: smsConsent, source: 'manual_staff_acknowledgement', note });
      toast.success(`SMS consent ${smsConsent ? 'recorded' : 'revoked'}`);
      setNote('');
      onUpdated?.(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-zinc-200" data-testid="customer-sms-consent-panel">
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-bold"><ShieldCheck className="h-4 w-4" /> SMS Consent</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="customer-sms-consent-status">
          {granted ? <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" /> Consent recorded</Badge> : optedOut ? <Badge className="border border-red-200 bg-red-50 text-red-700"><AlertTriangle className="mr-1 h-3 w-3" /> Opted out</Badge> : <Badge className="border border-amber-200 bg-amber-50 text-amber-800"><AlertTriangle className="mr-1 h-3 w-3" /> Missing consent</Badge>}
          {consent.sms_opt_in_at && <span className="text-xs text-zinc-500" data-testid="customer-sms-consent-date">Since {new Date(consent.sms_opt_in_at).toLocaleString()}</span>}
        </div>
        {latest && <p className="text-xs text-zinc-500" data-testid="customer-sms-consent-latest">Last update: {latest.staff_name || 'Team'} · {new Date(latest.created_at).toLocaleString()}</p>}
        <div className="space-y-1"><Label>Consent note</Label><Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="e.g., Customer verbally agreed during scheduling call" data-testid="customer-sms-consent-note-input" /></div>
        {error && <p className="rounded-sm border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="customer-sms-consent-error">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={() => updateConsent(true)} disabled={saving || optedOut} data-testid="customer-sms-consent-grant-button">Acknowledge Consent</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => updateConsent(false)} disabled={saving} data-testid="customer-sms-consent-revoke-button">Revoke / Mark No Consent</Button>
        </div>
      </CardContent>
    </Card>
  );
}